/**
 * Copies `text` to the clipboard, returning whether it worked.
 *
 * `navigator.clipboard` only exists in a secure context (https, or localhost).
 * On a plain-http origin the whole object is `undefined`, so calling
 * `navigator.clipboard.writeText` throws a TypeError *synchronously* — the
 * `.catch()` never sees it and the button looks like it did nothing. The
 * execCommand path below is the fallback for that case; it is deprecated but
 * still the only thing that works without TLS.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied / document not focused — try the legacy path below.
    }
  }

  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  // Keep it on-screen but invisible: display:none or visibility:hidden would
  // stop iOS/Safari from selecting the text, and off-screen coordinates can
  // make the selection scroll the page.
  ta.style.position = 'fixed';
  ta.style.top = '0';
  ta.style.left = '0';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  ta.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(ta);
  return ok;
}

